import { type Middleware, shift } from "@floating-ui/react";
import moment, { type Moment } from "moment";
import React from "react";
import ReactDatePicker, { type DatePickerProps as ReactDatePickerProps } from "react-datepicker";

type LegacyDateValue = Date | Moment | null | undefined;

type DatePickerProps = {
  [key: string]: unknown;
  dateFormat?: string;
  maxDate?: LegacyDateValue;
  minDate?: LegacyDateValue;
  onChange?: (date: Moment | null, event?: React.SyntheticEvent) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
  onSelect?: (date: Moment, event?: React.SyntheticEvent) => void;
  openToDate?: LegacyDateValue;
  placeholder?: string;
  placeholderText?: string;
  popperModifiers?: Middleware[];
  selected?: LegacyDateValue;
};

function toNativeDate(value: LegacyDateValue): Date | null | undefined {
  if (value instanceof Date) {
    return new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
  }

  if (moment.isMoment(value)) {
    return new Date(value.year(), value.month(), value.date());
  }

  return value;
}

function toNativeDateFormat(dateFormat: string | undefined): string | undefined {
  return dateFormat?.replaceAll("YYYY", "yyyy").replaceAll("DD", "dd");
}

function toLegacyMoment(value: Date | null): Moment | null {
  if (!value) {
    return null;
  }

  return moment({
    year: value.getFullYear(),
    month: value.getMonth(),
    date: value.getDate(),
  });
}

export default function DatePicker({
  dateFormat,
  maxDate,
  minDate,
  onChange,
  onKeyDown,
  onSelect,
  openToDate,
  placeholder,
  placeholderText,
  popperModifiers,
  selected,
  ...props
}: DatePickerProps) {
  const pickerRef = React.useRef<InstanceType<typeof ReactDatePicker>>(null);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    onKeyDown?.(event);
    if (event.key === "Escape") {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => pickerRef.current?.setFocus());
      });
    }
  };
  const nativeProps = {
    ...props,
    dateFormat: toNativeDateFormat(dateFormat),
    maxDate: toNativeDate(maxDate),
    minDate: toNativeDate(minDate),
    onChange: onChange
      ? (date: Date | null, event?: React.SyntheticEvent) => onChange(toLegacyMoment(date), event)
      : undefined,
    onKeyDown: handleKeyDown,
    onSelect: onSelect
      ? (date: Date, event?: React.SyntheticEvent) => {
          const legacyDate = toLegacyMoment(date);
          if (legacyDate) {
            onSelect(legacyDate, event);
          }
        }
      : undefined,
    openToDate: toNativeDate(openToDate),
    placeholderText: placeholderText ?? placeholder,
    popperModifiers: [shift({ padding: 8 }), ...(popperModifiers ?? [])],
    selected: toNativeDate(selected),
  } as ReactDatePickerProps;

  return <ReactDatePicker ref={pickerRef} {...nativeProps} />;
}
